import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlarmClock, Save, PlayCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const num = (v) => Number(v) || 0;

function LateFeeFields({ lf, set }) {
  return (
    <div className={`mt-5 grid grid-cols-2 gap-3 ${lf.enabled ? "" : "opacity-50"}`}>
      <div className="space-y-1.5"><Label className="text-xs">Grace period (days after due day)</Label>
        <Input type="number" min={0} max={27} value={lf.grace_days} onChange={(e) => set("grace_days", e.target.value)} data-testid="late-fee-grace" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Fee type</Label>
        <Select value={lf.fee_type} onValueChange={(v) => set("fee_type", v)}>
          <SelectTrigger data-testid="late-fee-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="flat">Flat amount</SelectItem><SelectItem value="percent">% of copay</SelectItem></SelectContent>
        </Select></div>
      {lf.fee_type === "percent" ? (
        <div className="space-y-1.5"><Label className="text-xs">Percent of copay</Label>
          <Input type="number" min={0} max={100} step="0.5" value={lf.percent} onChange={(e) => set("percent", e.target.value)} data-testid="late-fee-percent" /></div>
      ) : (
        <div className="space-y-1.5"><Label className="text-xs">One-time late fee ($)</Label>
          <Input type="number" min={0} value={lf.flat_amount} onChange={(e) => set("flat_amount", e.target.value)} data-testid="late-fee-flat" /></div>
      )}
      <div className="space-y-1.5"><Label className="text-xs">Extra per day after that ($, 0 = none)</Label>
        <Input type="number" min={0} value={lf.daily_amount} onChange={(e) => set("daily_amount", e.target.value)} data-testid="late-fee-daily" /></div>
    </div>
  );
}

function LateFeeSummary({ lf }) {
  const feeText = lf.fee_type === "percent" ? `${num(lf.percent)}% of the copay` : fmtMoney(lf.flat_amount);
  const daily = num(lf.daily_amount) > 0 ? <>, plus <b>{fmtMoney(lf.daily_amount)}/day</b> for every additional day it stays unpaid</> : null;
  return (
    <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-100 p-3.5" data-testid="late-fee-summary">
      <AlarmClock className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
      <p className="text-xs text-amber-900 leading-relaxed">
        {lf.enabled
          ? <>A <b>{feeText}</b> late fee posts once per month when the copay is still unpaid <b>{num(lf.grace_days)} day(s)</b> after the rent due day{daily}. Fees appear on the tenant's balance and in your Rent & Section 8 ledger, where you can waive them.</>
          : "Automatic late fees are off. You can still add a fee manually from the ledger."}
      </p>
    </div>
  );
}

export function LateFeeSettings({ onPosted }) {
  const [lf, setLf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { api.get("/settings/late-fees").then((r) => setLf(r.data)); }, []);
  const set = (k, v) => setLf((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { owner_id, ...payload } = lf;
      const { data } = await api.put("/settings/late-fees", {
        ...payload, grace_days: num(payload.grace_days), flat_amount: num(payload.flat_amount),
        percent: num(payload.percent), daily_amount: num(payload.daily_amount),
      });
      setLf(data);
      toast.success("Late fee rules saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/late-fees/run");
      toast.success(data.count ? `Posted ${data.count} late fee(s) to tenant ledgers` : "No past-due copays right now — nothing posted");
      onPosted?.(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setRunning(false); }
  };

  if (!lf) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="late-fee-settings">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">Automatic late fees</h2>
          <p className="text-sm text-slate-500">Posts to the tenant's ledger when the copay is still unpaid after the grace period. Never applied to Section 8 HAP.</p>
        </div>
        <Switch checked={lf.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="late-fee-enabled" />
      </div>
      <LateFeeFields lf={lf} set={set} />
      <LateFeeSummary lf={lf} />
      <div className="flex gap-2 mt-4">
        <Button variant="outline" className="gap-2" onClick={runNow} disabled={running} data-testid="late-fee-run-now">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Check now
        </Button>
        <Button className="gap-2 flex-1" onClick={save} disabled={saving} data-testid="late-fee-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save late fee rules"}</Button>
      </div>
    </div>
  );
}
